/**
 * airan-ntn — FedSGD Implementation
 */

import { FLStrategy, ModelWeights, TrainSample, SimConfig, UserEquipment } from '../types.js';
import { random, randn } from '../channel.js';
import { cloneWeights, addWeights, scaleWeights, paramCount, computeGradient } from '../model.js';

export class FedSGD implements FLStrategy {
  readonly name = 'FedSGD' as const;

  localTrain(
    ue: UserEquipment,
    globalWeights: ModelWeights,
    data: TrainSample[],
    config: SimConfig
  ): void {
    if (data.length === 0) return;

    // Compute gradient on local data using global weights
    const { gW, gB } = computeGradient(globalWeights, data);

    // Send gradient to server (simulated by storing in localWeights)
    ue.localWeights = {
      w: gW,
      b: gB,
      numFeatures: globalWeights.numFeatures,
      numClasses: globalWeights.numClasses,
      version: globalWeights.version
    };
  }

  aggregate(
    globalWeights: ModelWeights,
    participants: UserEquipment[],
    _allData: Map<number, TrainSample[]>,
    config: SimConfig
  ): ModelWeights {
    if (participants.length === 0) return globalWeights;

    // Average gradients
    const avgGradW = new Float32Array(globalWeights.w.length).fill(0);
    const avgGradB = new Float32Array(globalWeights.b.length).fill(0);

    for (const ue of participants) {
      for (let i = 0; i < globalWeights.w.length; i++) {
        avgGradW[i] += ue.localWeights.w[i];
      }
      for (let i = 0; i < globalWeights.b.length; i++) {
        avgGradB[i] += ue.localWeights.b[i];
      }
    }

    for (let i = 0; i < avgGradW.length; i++) avgGradW[i] /= participants.length;
    for (let i = 0; i < avgGradB.length; i++) avgGradB[i] /= participants.length;

    // Apply gradient to global weights
    const newWeights = cloneWeights(globalWeights);
    for (let i = 0; i < newWeights.w.length; i++) {
      newWeights.w[i] -= config.learningRate * avgGradW[i];
    }
    for (let i = 0; i < newWeights.b.length; i++) {
      newWeights.b[i] -= config.learningRate * avgGradB[i];
    }
    newWeights.version = globalWeights.version + 1;

    return newWeights;
  }
}